import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const { resetPasswordForEmail } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await resetPasswordForEmail(email);
      if (error) throw error;
      setResetEmailSent(true);
    } catch (error: any) {
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Company Name Header */}
      <div className="bg-white w-full border-b border-black/10">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <img src="/logo.svg" alt="Logo" className="h-8" />
        </div>
      </div>

      {/* Full-width Red Header Bar */}
      <div className="bg-red-700 w-full">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <h1 className="text-white text-xl font-medium">
            {t('auth.resetYourPassword')}
          </h1>
          <div className="[&_button]:bg-transparent [&_button]:border-white/20 [&_button]:hover:border-white/40 [&_button]:text-white [&_svg]:text-white/60 [&_.absolute]:bg-white [&_.absolute]:border-black/15 [&_.absolute]:rounded-none [&_.absolute_button]:hover:bg-black/5 [&_.absolute_button]:text-black [&_.absolute_button.bg-slate-700\\/50]:bg-transparent [&_.absolute_button.bg-slate-700\\/50]:text-red-700 [&_.absolute_button.bg-slate-700\\/50]:border-l-2 [&_.absolute_button.bg-slate-700\\/50]:border-l-red-700 [&_.absolute_svg.text-blue-400]:text-red-700">
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white max-w-7xl mx-auto px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* LEFT COLUMN - Reset Password Form */}
          <div className="max-w-md">
            <p className="text-black/70 mb-8">
              {t('auth.wellSendYouLink')}
            </p>

            {error && (
              <div className="mb-6 text-sm text-red-700 border-l-2 border-red-700 pl-4 py-2">
                {error}
              </div>
            )}

            {!resetEmailSent ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">{t('auth.email')}</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black/30" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth.enterYourEmail')}
                      className="w-full bg-white text-black pl-10 pr-4 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors placeholder:text-black/30"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#d91222] text-white hover:text-red-900 disabled:text-black/20 py-4 font-semibold transition-colors flex items-center justify-center gap-2 mt-2 border border-red-700 hover:border-red-900 disabled:border-black/10 rounded"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black/10 border-t-red-700 rounded-full animate-spin" />
                  ) : (
                    t('auth.resetPassword')
                  )}
                </button>

                <Link
                  to="/signin"
                  className="w-full flex items-center justify-center gap-2 text-black/70 hover:text-red-700 transition-colors py-2"
                >
                  <ArrowLeft size={16} />
                  {t('auth.backToSignIn')}
                </Link>

                <div className="mt-4 text-xs text-black/50">
                  {t('auth.secureConnection')}
                </div>
              </form>
            ) : (
              <div className="py-6">
                <div className="w-16 h-16 border-2 border-green-600 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h3 className="text-2xl font-semibold text-black mb-3">{t('auth.checkYourEmail')}</h3>
                <p className="text-black/70 mb-6">
                  {t('auth.weveSentPasswordReset')} <span className="text-black font-medium">{email}</span>
                </p>
                <Link
                  to="/signin"
                  className="inline-block bg-[#d91222] text-white hover:text-red-900 px-6 py-3 rounded font-semibold transition-colors border border-red-700 hover:border-red-900"
                >
                  {t('auth.returnToSignIn')}
                </Link>
                <p className="text-sm text-black/70 mt-4">
                  {t('auth.didntReceiveEmail')}{' '}
                  <button
                    type="button"
                    onClick={() => setResetEmailSent(false)}
                    className="text-red-700 hover:text-red-900 transition-colors underline"
                  >
                    {t('auth.tryAgain')}
                  </button>
                </p>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN - Image */}
          <div className="flex flex-col items-center justify-center text-center space-y-6">
            <img
              src="https://sunny-gaufre-664069.netlify.app/capital/en/1.png"
              alt="Security"
              className="w-full rounded-lg"
            />
            <p className="text-lg text-black/70 font-medium">
              Secure password recovery for your account
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;